import { XLINK_NS } from './constants';

// cspell: ignore SVGIDs

/**
 * 清除简单图形素材自带的填充和描边属性。
 *
 * @param shape - 需要清理的图形节点。
 * @returns 清理后的同一图形节点。
 */
export const removePaintAttributes = <T extends SVGElement>(shape: T): T => {
    Array.from(shape.attributes).forEach((attribute) => {
        if (attribute.name.includes('fill') || attribute.name.includes('stroke')) {
            shape.removeAttribute(attribute.name);
        }
    });

    return shape;
};

/**
 * 为 SVG 子树中的全部 ID 添加前缀，并同步重写属性与样式中的对应引用。
 *
 * 该函数原地修改传入节点；前缀的唯一性由调用方负责。
 *
 * @param root - 需要隔离 ID 与引用的 SVG 子树根节点。
 * @param prefix - 添加到每个原始 ID 前的唯一前缀。
 * @returns 完成 ID 与引用重写的同一根节点。
 */
export const rewriteSVGIDsWithPrefix = <T extends SVGElement>(root: T, prefix: string): T => {
    const idMap = new Map<string, string>();
    const elements = [root, ...Array.from(root.querySelectorAll('*'))];

    elements.forEach((element) => {
        const id = element.getAttribute('id');
        if (!id) return;

        const nextID = `${prefix}-${id}`;
        idMap.set(id, nextID);
        element.setAttribute('id', nextID);
    });

    // 优先处理较长的 ID，避免 ID 存在包含关系时，短 ID 先替换导致长 ID 无法匹配。
    const idEntries = [...idMap.entries()].sort(([left], [right]) => right.length - left.length);

    /**
     * 按字面值替换全部匹配内容，避免正则字符和替换字符串占位符影响 ID。
     *
     * @param source - 待处理的原始文本。
     * @param search - 需要匹配的字面值。
     * @param replacement - 用于替换匹配项的字面值。
     * @returns 替换全部匹配项后的文本。
     */
    const replace = (source: string, search: string, replacement: string): string => {
        return source.split(search).join(replacement);
    };

    // ID 既可能出现在 url(#id) 中，也可能通过 href="#id" 引用。逐属性重写可以
    // 保留未知 SVG 标签，而不用针对每种定义维护一套容易遗漏的白名单。
    elements.forEach((element) => {
        Array.from(element.attributes).forEach((attribute) => {
            let value = attribute.value;

            idEntries.forEach(([id, nextID]) => {
                value = replace(value, `url(#${id})`, `url(#${nextID})`);
                value = replace(value, `#${id}`, `#${nextID}`);
            });

            if (attribute.namespaceURI === XLINK_NS) {
                element.setAttributeNS(XLINK_NS, attribute.name, value);
            } else {
                element.setAttribute(attribute.name, value);
            }
        });

        if (element.tagName.toLowerCase() === 'style' && element.textContent) {
            let content = element.textContent;

            idEntries.forEach(([id, nextID]) => {
                content = replace(content, `#${id}`, `#${nextID}`);
            });

            element.textContent = content;
        }
    });

    return root;
};
